import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import clsx from 'clsx';
import { createUseStyles } from 'react-jss';

import Box, { BoxProps } from 'components/DesignSystem/Box/Box';
import IconButton from 'DesignSystem2/Button/IconButton';
import { PillGroupHandle } from 'DesignSystem2/Pill/types';
import { isTouchEnabled } from 'utils/device';

import { Breakpoint, Palette } from '../Theme';

const PAGE_SIZE = 240; // unit: pixels

const LAST_PAGE_CLICK_DEBOUNCE_DELAY = 400; // unit: milliseconds

const useStyles = createUseStyles({
    pillsStretchToFit: {
        '& > *': {
            flexGrow: 1,
            flexBasis: 0,
        },
    },
    paginationButtonContainer: {
        display: 'flex',
        height: '100%',
        width: '88px',
        position: 'absolute',
        alignItems: 'center',
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
        opacity: 1,
        zIndex: 2,
    },
    fade: { opacity: 0, transition: `opacity ${LAST_PAGE_CLICK_DEBOUNCE_DELAY}ms` },
    leftPaginationButtonContainer: {
        composes: '$paginationButtonContainer',
        left: '-2px',
        justifyContent: 'start',
        background: `linear-gradient(to right, ${Palette.Surface.x10} 40px, rgba(255, 255, 255, 0))`,
        [Breakpoint.down.sm]: {
            background: `linear-gradient(to right, ${Palette.Surface.x10} 16px, rgba(255, 255, 255, 0))`,
        },
    },
    rightPaginationButtonContainer: {
        composes: '$paginationButtonContainer',
        right: '-2px',
        textAlign: 'right',
        justifyContent: 'end',
        background: `linear-gradient(to left, ${Palette.Surface.x10} 40px, rgba(255, 255, 255, 0))`,
        [Breakpoint.down.sm]: {
            background: `linear-gradient(to left, ${Palette.Surface.x10} 16px, rgba(255, 255, 255, 0))`,
        },
    },
});

type PaginationButtonStatus = 'enabled' | 'disabled' | 'hidden';

interface PillGroupProps extends BoxProps {
    pills: JSX.Element[];
    stretchToFit?: boolean;
    hidePaginationButtons?: boolean;
    wrap?: boolean;
    smallGap?: boolean;
    fullWidth?: boolean;
    forceRightGradient?: boolean;
    forceLeftGradient?: boolean;
}

const PillGroup = forwardRef(
    (
        {
            pills,
            className,
            onScroll,
            stretchToFit = false,
            hidePaginationButtons,
            wrap,
            smallGap,
            fullWidth,
            forceRightGradient,
            forceLeftGradient,
            ...boxProps
        }: PillGroupProps,
        ref: React.Ref<PillGroupHandle>,
    ): JSX.Element => {
        const classes = useStyles();

        const leftPaginationDebounceTimer = useRef<NodeJS.Timeout>();
        const rightPaginationDebounceTimer = useRef<NodeJS.Timeout>();
        const [leftPaginationStatus, setLeftPaginationStatus] = useState<PaginationButtonStatus>('hidden');
        const [rightPaginationStatus, setRightPaginationStatus] = useState<PaginationButtonStatus>('hidden');

        const pillsRef = useRef<HTMLDivElement | null>(null);
        const scrollContainerRef = useRef<HTMLDivElement>(null);
        const updatePaginationButtons = (): void => {
            if (!scrollContainerRef.current || !pillsRef.current) return;

            const containerScrollLeft = scrollContainerRef.current.scrollLeft;
            const containerWidth = scrollContainerRef.current.clientWidth;
            const pillsWidth = pillsRef.current.scrollWidth;

            const acceptableMarginOfErrorToAccountForBrowserDifferences = 1.001;

            const shouldShowLeftPaginationButton =
                containerScrollLeft > acceptableMarginOfErrorToAccountForBrowserDifferences;
            const shouldShowRightPaginationButton =
                pillsWidth - containerScrollLeft - containerWidth >
                acceptableMarginOfErrorToAccountForBrowserDifferences;

            if (shouldShowLeftPaginationButton) {
                if (leftPaginationDebounceTimer.current) clearTimeout(leftPaginationDebounceTimer.current);
                setLeftPaginationStatus('enabled');
            } else if (leftPaginationStatus === 'enabled') {
                setLeftPaginationStatus('disabled');
                leftPaginationDebounceTimer.current = setTimeout(
                    () => setLeftPaginationStatus('hidden'),
                    LAST_PAGE_CLICK_DEBOUNCE_DELAY,
                );
            }

            if (shouldShowRightPaginationButton) {
                if (rightPaginationDebounceTimer.current) clearTimeout(rightPaginationDebounceTimer.current);
                setRightPaginationStatus('enabled');
            } else if (rightPaginationStatus === 'enabled') {
                setRightPaginationStatus('disabled');
                rightPaginationDebounceTimer.current = setTimeout(
                    () => setRightPaginationStatus('hidden'),
                    LAST_PAGE_CLICK_DEBOUNCE_DELAY,
                );
            }
        };

        const makePaginationButton = (left: boolean): JSX.Element => (
            <div
                key={left ? 'left' : 'right'}
                className={clsx(
                    left ? classes.leftPaginationButtonContainer : classes.rightPaginationButtonContainer,
                    (left ? leftPaginationStatus === 'disabled' : rightPaginationStatus === 'disabled') && classes.fade,
                )}
            >
                {!isTouchEnabled && (
                    <IconButton
                        icon={left ? 'ChevronLeft' : 'ChevronRight'}
                        rank={'tertiary'}
                        size={'tiny'}
                        iconSize={'20px'}
                        onClick={(): void => {
                            scrollContainerRef.current?.scrollBy({
                                left: left ? -PAGE_SIZE : +PAGE_SIZE,
                                behavior: 'smooth',
                            });
                        }}
                        disabled={left ? leftPaginationStatus === 'disabled' : rightPaginationStatus === 'disabled'}
                        buttonProps={{ style: { pointerEvents: 'auto' } }}
                    />
                )}
            </div>
        );

        // Check whether pagination buttons should be shown on first render (before any scrolling happens)
        // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        useEffect(() => updatePaginationButtons(), [pills.length]);
        // Check whether pagination buttons should be shown whenever the scroll container resizes
        const resizeObserverRef = React.useRef<ResizeObserver>(
            typeof window !== 'undefined' ? new ResizeObserver(updatePaginationButtons) : null,
        );
        const pillsCallbackRef = React.useCallback(
            (pills: HTMLDivElement) => {
                pillsRef.current = pills;
                const resizeObserver = resizeObserverRef.current;
                if (pills !== null && resizeObserver) resizeObserver.observe(pills);
                else if (resizeObserver) resizeObserver.disconnect();
            },
            // TODO: check or fix hooks error
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [resizeObserverRef.current],
        );

        const paginationButtons = [
            leftPaginationStatus !== 'hidden' && (!isTouchEnabled || forceLeftGradient) && makePaginationButton(true),
            rightPaginationStatus !== 'hidden' &&
                (!isTouchEnabled || forceRightGradient) &&
                makePaginationButton(false),
        ];

        // Pill can be externally scrolled to using `pillGroupRef.current.scrollIntoView(pillRef)`
        useImperativeHandle(ref, () => ({
            scrollIntoView: (pill): void => {
                if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollLeft = pill.offsetLeft - 104;
                }
            },
        }));

        return (
            <Box
                className={clsx(className, 'relative max-w-full')}
                {...boxProps}
                style={{ minWidth: stretchToFit ? '100%' : 0 }}
            >
                {!hidePaginationButtons && paginationButtons}
                <Box
                    className={'overflow-x-auto scrollbar-none [-ms-overflow-style:none] [scrollbar-width:none]'}
                    ref={scrollContainerRef}
                    onScroll={(e): void => {
                        if (!!onScroll) onScroll(e);
                        updatePaginationButtons();
                    }}
                >
                    <Box
                        className={clsx(
                            'inline-flex gap-1 flex-nowrap',
                            stretchToFit && classes.pillsStretchToFit,
                            wrap && 'flex-wrap',
                            smallGap && 'gap-0.25',
                            fullWidth && 'w-full',
                        )}
                        ref={pillsCallbackRef}
                    >
                        {pills.map((pill, index) => React.cloneElement(pill, { key: index }))}
                    </Box>
                </Box>
            </Box>
        );
    },
);

PillGroup.displayName = 'PillGroup';
export default PillGroup;
